import { useCallback, useEffect, useRef, useState } from 'react';

import { CfVerificationRetry, VERIFICATION_RETRY_DELAY_MS, VERIFICATION_RETRY_NOTICE_MS } from './cfVerificationRetry';

export type CfVerificationRetryControl = {
  retry: CfVerificationRetry;
  schedule: () => void;
  runNow: () => void;
};

/** `check` resolves whether the domain verified, and rejects when the request never got an answer. */
export const useCfVerificationRetry = (
  check: () => Promise<boolean>,
  verificationInPlay: boolean,
): CfVerificationRetryControl => {
  const [retry, setRetry] = useState<CfVerificationRetry>('none');
  const currentRun = useRef(0);

  const abandon = useCallback(() => {
    currentRun.current += 1;
    setRetry('none');
  }, []);

  const schedule = useCallback(() => setRetry('scheduled'), []);

  const runNow = useCallback(async () => {
    currentRun.current += 1;
    const run = currentRun.current;
    setRetry('running');

    const outcome = await check().then(
      () => 'none' as const,
      () => 'errored' as const,
    );

    if (run === currentRun.current) setRetry(outcome);
  }, [check]);

  useEffect(() => {
    if (retry !== 'scheduled') return undefined;

    const timer = setTimeout(runNow, VERIFICATION_RETRY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [retry, runNow]);

  useEffect(() => {
    if (retry !== 'running') return undefined;

    const timer = setTimeout(abandon, VERIFICATION_RETRY_NOTICE_MS);

    return () => clearTimeout(timer);
  }, [retry, abandon]);

  useEffect(() => {
    if (!verificationInPlay) abandon();
  }, [verificationInPlay, abandon]);

  return { retry, schedule, runNow };
};
